/**
 * Email Capture Manager Module
 * Handles Mail.exe email capture flow with stacked error windows
 */

// Prevent redeclaration if script is loaded multiple times
if (typeof EmailCaptureManager === 'undefined') {
    class EmailCaptureManager {
        constructor() {
            this.errorStack = [
                { code: '015', message: 'Illegal Backhand Detected' },
                { code: '777', message: 'Heaven Mode Activated' },
                { code: '222', message: 'Ball Kid Timeout' },
                { code: '69', message: 'Nice Volley' },
                { code: '404', message: 'Lost the Match' }
            ];
            this.currentStep = 1; // 1 = form, 2 = success
            this.email = '';
            this.discountCode = 'ACE-20';
            this.windowIds = [];
            this.errorWindowIds = [];
            this.isCreatingErrors = false; // Track if error animation is in progress
            this.shouldCancelAnimation = false; // Flag to cancel animation
            this.resizeHandler = null; // Store resize handler for cleanup
            this.dataAttributes = {}; // Store window settings from email-app section
        }

        /**
         * Collect data attributes from the email-app section
         */
        collectSettings() {
            const contentDiv = document.querySelector('.st-email-app-content');
            if (contentDiv && window.smashOS?.windowManager) {
                this.dataAttributes = window.smashOS.windowManager.collectDataAttributes(contentDiv);
                console.log('[EmailCaptureManager] Collected settings from email-app section:', this.dataAttributes);
            }
        }

        /**
         * Start email capture flow
         */
        async startEmailFlow() {
            // Ensure clean state on re-entry (prevents orphan DOM from prior runs)
            this.errorWindowIds = [];
            this.windowIds = [];

            // Collect settings first
            this.collectSettings();

            // Get discount code from theme settings
            if (typeof theme !== 'undefined' && theme.settings && theme.settings.discount_code) {
                this.discountCode = theme.settings.discount_code;
            }

            // Step 0: Close all existing windows
            this.closeAllExistingWindows();

            // Step 1: Hide desktop icons and taskbar
            this.hideDesktopUI();

            // Step 2: Show main window first (email form)
            await this.showMainWindow(1);

            // Step 3: Create 5 error windows sequentially behind main window
            // Don't await - let it run while user can interact
            this.createErrorStack();
        }

        /**
         * Hide desktop icons and taskbar
         */
        hideDesktopUI() {
            const desktopIcons = document.querySelector('.st-desktop__icons');
            const taskbar = document.querySelector('.st-taskbar');
            const desktopLogo = document.querySelector('.st-desktop__logo');
            const desktopCartIcon = document.getElementById('desktop-cart-icon');

            if (desktopIcons) {
                desktopIcons.classList.remove('visible');
            }
            if (taskbar) {
                taskbar.classList.remove('visible');
            }
            if (desktopLogo) {
                desktopLogo.classList.remove('visible');
            }
            if (desktopCartIcon) {
                desktopCartIcon.classList.remove('visible');
            }
        }

        /**
         * Show desktop icons and taskbar
         */
        showDesktopUI() {
            const desktopIcons = document.querySelector('.st-desktop__icons');
            const taskbar = document.querySelector('.st-taskbar');
            const desktopLogo = document.querySelector('.st-desktop__logo');
            const desktopCartIcon = document.getElementById('desktop-cart-icon');

            if (desktopIcons) {
                desktopIcons.classList.add('visible');
            }
            if (taskbar) {
                taskbar.classList.add('visible');
            }
            if (desktopLogo) {
                desktopLogo.classList.add('visible');
            }
            if (desktopCartIcon) {
                desktopCartIcon.classList.add('visible');
            }
        }

        /**
         * Create 5 error windows stacked behind main window
         * Positioned with offset so only titlebars are visible
         */
        async createErrorStack() {
            if (!window.smashOS || !window.smashOS.windowManager) {
                console.error('EmailCaptureManager: WindowManager not available');
                return;
            }

            // Reset cancellation flag and mark animation as in progress
            this.shouldCancelAnimation = false;
            this.isCreatingErrors = true;

            // Get main window position to stack errors behind it
            const mainWindowElement = document.getElementById(`window-${this.mainWindowId}`);
            if (!mainWindowElement) {
                this.isCreatingErrors = false;
                return;
            }

            const mainRect = mainWindowElement.getBoundingClientRect();
            // Use main window size for all error windows
            const windowWidth = mainRect.width;
            const windowHeight = mainRect.height;
            const titlebarHeight = 30; // Approximate titlebar height
            const offsetX = 20; // Horizontal offset between windows
            const offsetY = titlebarHeight + 2; // Vertical offset to show only titlebar

            // Create exactly 5 error windows stacked behind main window in bulk
            // Iterate in reverse (4 to 0) so email-error-bg-0 is created last
            // Requirements:
            // - IDs: email-error-bg-0 to email-error-bg-4 (created in reverse: 4, 3, 2, 1, 0)
            // - Z-index: decrease from 1025 to 1017 (email-error-bg-0: 1025, email-error-bg-4: 1017)
            // - Position: top increases (email-error-bg-0 lowest top, email-error-bg-4 highest top)

            // Create all windows in parallel
            const windowPromises = [];
            for (let i = 4; i >= 0; i--) {
                const error = this.errorStack[i];
                const windowId = `email-error-bg-${i}`;

                // Calculate position: stack behind main window with offset
                // email-error-bg-0 has lowest top value (closest to main, just above it)
                // email-error-bg-4 has highest top value (farthest from main, higher up)
                // Each window is offset to the left and upward so only titlebar is visible
                const x = mainRect.left - (offsetX * (i + 1)); // Stack to the left
                const y = mainRect.top - (offsetY * (i + 1)); // i=0: lowest top (mainRect.top - offsetY), i=4: highest top (mainRect.top - 5*offsetY)

                // Create HTML content for background error window
                const errorContent = `
        <div class="st-email-error-background">
          <div class="st-email-error-background__content">
            <div class="st-email-error-background__icon">⚠</div>
            <div class="st-email-error-background__message">
              <p class="st-email-error-background__title">Error ${error.code}: ${error.message}</p>
            </div>
          </div>
        </div>
      `;

                // Create window with z-index decreasing from 1025 to 1017
                // email-error-bg-0: 1025 (highest), email-error-bg-4: 1017 (lowest)
                // All still below main window (10000)
                const windowPromise = window.smashOS.windowManager.open(windowId, {
                    title: `▲ Error ${error.code}: ${error.message}`,
                    icon: null,
                    content: errorContent,
                    size: { width: `${windowWidth}px`, height: `${windowHeight}px` },
                    position: { x: `${x}px`, y: `${y}px` },
                    isModal: false,
                    canResize: false,
                    canMaximize: false,
                    zIndex: 1025 - (i * 2), // email-error-bg-0: 1025, email-error-bg-1: 1023, email-error-bg-2: 1021, email-error-bg-3: 1019, email-error-bg-4: 1017 (all below main: 10000)
                    showStatusBar: false,
                    showMenuBar: false
                }).then(() => {
                    // Set error window titlebar to inactive (should not be active)
                    const errorWindowElement = document.getElementById(`window-${windowId}`);
                    if (errorWindowElement) {
                        // Hide window by default
                        errorWindowElement.style.display = 'none';

                        const errorTitlebar = errorWindowElement.querySelector('.st-window__titlebar');
                        if (errorTitlebar) {
                            errorTitlebar.classList.remove('st-titlebar--active');
                            errorTitlebar.classList.add('st-titlebar--inactive');
                        }
                    }
                    return windowId;
                });

                windowPromises.push(windowPromise);
                this.errorWindowIds.push(windowId);
                this.windowIds.push(windowId);
            }

            // Wait for all windows to be created
            await Promise.all(windowPromises);

            // Display windows sequentially from email-error-bg-0 to email-error-bg-4
            for (let i = 0; i < 5; i++) {
                const windowId = `email-error-bg-${i}`;
                const errorWindowElement = document.getElementById(`window-${windowId}`);
                if (errorWindowElement) {
                    // Show window by adding visible class
                    errorWindowElement.classList.add('visible');
                    errorWindowElement.style.display = '';
                }

                // Add delay between each window display (except for the last one)
                if (i < 4) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
            }

            // Animation completed
            this.isCreatingErrors = false;

            // Ensure main window stays active after all error windows are created
            this.ensureMainWindowActive();

            // Setup resize handler to keep error windows aligned with main window
            this.setupResizeHandler();
        }

        /**
         * Update error window positions relative to main window
         */
        updateErrorWindowPositions() {
            const mainWindowElement = document.getElementById(`window-${this.mainWindowId}`);
            if (!mainWindowElement || this.errorWindowIds.length === 0) {
                return;
            }

            const mainRect = mainWindowElement.getBoundingClientRect();
            const titlebarHeight = 30; // Approximate titlebar height
            const offsetX = 20; // Horizontal offset between windows
            const offsetY = titlebarHeight + 2; // Vertical offset to show only titlebar

            // Update position for each error window
            for (let i = 0; i < 5; i++) {
                const windowId = `email-error-bg-${i}`;
                const errorWindowElement = document.getElementById(`window-${windowId}`);
                if (!errorWindowElement) continue;

                // Recalculate position based on current main window position
                const x = mainRect.left - (offsetX * (i + 1)); // Stack to the left
                const y = mainRect.top - (offsetY * (i + 1)); // Stack upward

                // Update position
                errorWindowElement.style.left = `${x}px`;
                errorWindowElement.style.top = `${y}px`;

                // Update in window manager if available
                if (window.smashOS && window.smashOS.windowManager) {
                    window.smashOS.windowManager.updatePosition(windowId, { x: `${x}px`, y: `${y}px` });
                }
            }
        }

        /**
         * Setup resize handler to keep error windows aligned with main window
         */
        setupResizeHandler() {
            // Remove existing handler if any
            if (this.resizeHandler) {
                window.removeEventListener('resize', this.resizeHandler);
            }

            // Create new resize handler
            this.resizeHandler = () => {
                // Only update if error windows exist
                if (this.errorWindowIds.length > 0) {
                    // Use a small timeout to let WindowManager finish its repositioning first
                    setTimeout(() => {
                    this.updateErrorWindowPositions();
                    }, 50);
                }
            };

            // Attach resize listener
            window.addEventListener('resize', this.resizeHandler);
        }

        /**
         * Clean up resize handler
         */
        cleanupResizeHandler() {
            if (this.resizeHandler) {
                window.removeEventListener('resize', this.resizeHandler);
                this.resizeHandler = null;
            }
        }

        /**
         * Main window ID - single window that changes content
         */
        get mainWindowId() {
            return 'email-capture-main';
        }

        /**
         * Show or update main window with step content
         * @param {number} step - 1 = form, 2 = success
         */
        async showMainWindow(step) {
            if (!window.smashOS || !window.smashOS.windowManager) {
                console.error('EmailCaptureManager: WindowManager not available');
                return;
            }

            this.currentStep = step;

            const stepConfig = {
                1: {
                    title: 'Smash Communication Utility',
                    section: 'window-email-form',
                    width: '450px'
                },
                2: {
                    title: 'Transmission Complete',
                    section: 'window-email-success',
                    width: '400px'
                }
            };

            const config = stepConfig[step];
            if (!config) {
                console.error('EmailCaptureManager: Invalid step:', step);
                return;
            }

            // Check if window already exists
            const existingWindow = document.getElementById(`window-${this.mainWindowId}`);

            if (existingWindow) {
                // Update existing window content
                await this.updateWindowContent(this.mainWindowId, config.section, config.title);

                // Ensure main window is active and stays on top after content update
                setTimeout(() => {
                    this.ensureMainWindowActive();
                    this.ensureMainWindowOnTop();
                }, 50);
            } else {
                // Create new window with highest z-index
                await window.smashOS.windowManager.open(this.mainWindowId, {
                    title: config.title,
                    icon: null,
                    section: config.section,
                    size: { width: config.width, height: 'auto' },
                    position: { x: '50%', y: '50%' },
                    isModal: true,
                    canResize: false,
                    canMaximize: false,
                    zIndex: 10000, // Much higher than background errors (1000-1004)
                    showStatusBar: false,
                    showMenuBar: false,
                    dataAttributes: this.dataAttributes // Pass collected settings
                });

                this.windowIds.push(this.mainWindowId);

                // Intercept close button
                this.interceptCloseButton(this.mainWindowId);

                // Ensure main window is active and stays on top
                setTimeout(() => {
                    this.ensureMainWindowActive();
                    this.ensureMainWindowOnTop();
                }, 50);
            }

            // Setup event handlers for current step
            setTimeout(() => {
                this.setupStepHandlers(step);
            }, 100);

            // Ensure main window is active (in case error windows were created after)
            setTimeout(() => {
                this.ensureMainWindowActive();
            }, 200);
        }

        /**
         * Ensure main window titlebar is active
         */
        ensureMainWindowActive() {
            const mainWindowElement = document.getElementById(`window-${this.mainWindowId}`);
            if (mainWindowElement) {
                const mainTitlebar = mainWindowElement.querySelector('.st-window__titlebar');
                if (mainTitlebar) {
                    // Remove active from all titlebars first
                    document.querySelectorAll('.st-window__titlebar').forEach(tb => {
                        tb.classList.remove('st-titlebar--active');
                        tb.classList.add('st-titlebar--inactive');
                    });
                    // Set main window as active
                    mainTitlebar.classList.add('st-titlebar--active');
                    mainTitlebar.classList.remove('st-titlebar--inactive');
                }
            }
        }

        /**
         * Update window content without closing/reopening
         */
        async updateWindowContent(windowId, sectionName, newTitle) {
            const windowElement = document.getElementById(`window-${windowId}`);
            if (!windowElement) return;

            // Update title
            const titleElement = windowElement.querySelector('.st-titlebar__title');
            if (titleElement) {
                titleElement.textContent = newTitle;
            }

            // Update content using windowManager's loadSectionContent
            if (window.smashOS && window.smashOS.windowManager) {
                await window.smashOS.windowManager.loadSectionContent(windowElement, sectionName);
            }

            // Ensure window stays on top
            this.ensureMainWindowOnTop();
        }

        /**
         * Ensure main window has highest z-index
         */
        ensureMainWindowOnTop() {
            const windowElement = document.getElementById(`window-${this.mainWindowId}`);
            if (!windowElement) return;

            // Set very high z-index to ensure it's always on top
            // Use !important via inline style to override any CSS
            windowElement.style.setProperty('z-index', '10000', 'important');

            // Also update in window manager to prevent bringToFront from overriding
            if (window.smashOS && window.smashOS.windowManager) {
                const windowData = window.smashOS.windowManager.windows.get(this.mainWindowId);
                if (windowData) {
                    windowData.zIndex = 10000;
                    // Prevent zIndexCounter from overriding this
                    windowData.fixedZIndex = true;
                }
            }
        }

        /**
         * Intercept close button to trigger cancel action
         */
        interceptCloseButton(windowId) {
            const interceptClose = () => {
                const windowElement = document.getElementById(`window-${windowId}`);
                if (!windowElement) {
                    setTimeout(interceptClose, 50);
                    return;
                }

                // Listen for window:close event
                const closeHandler = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.handleCancel();
                };

                // Remove existing listener if any
                windowElement.removeEventListener('window:close', closeHandler);
                windowElement.addEventListener('window:close', closeHandler, { capture: true });

                // Also intercept the close button directly
                const closeButton = windowElement.querySelector('[data-action="close"]');
                if (closeButton) {
                    // Remove existing listeners by cloning
                    const newButton = closeButton.cloneNode(true);
                    closeButton.parentNode.replaceChild(newButton, closeButton);

                    newButton.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.handleCancel();
                    });
                }
            };

            // Try immediately, then retry if needed
            interceptClose();
        }

        /**
         * Setup event handlers for current step
         */
        setupStepHandlers(step) {
            const setupHandlers = () => {
                const windowElement = document.getElementById(`window-${this.mainWindowId}`);
                if (!windowElement) {
                    setTimeout(setupHandlers, 50);
                    return;
                }

                if (step === 1) {
                    // Step 1: Email form
                    const formContainer = windowElement.querySelector('#email-form-window');
                    if (!formContainer) {
                        setTimeout(setupHandlers, 50);
                        return;
                    }

                    const newContainer = formContainer.cloneNode(true);
                    formContainer.parentNode.replaceChild(newContainer, formContainer);

                    newContainer.addEventListener('click', (e) => {
                        e.stopPropagation();
                        const button = e.target.closest('button');
                        if (!button) return;

                        const action = button.dataset.action;
                        if (action === 'cancel') {
                            this.handleCancel();
                        } else if (action === 'submit') {
                            this.handleEmailSubmit();
                        }
                    });

                    const form = newContainer.querySelector('#email-capture-form');
                    if (form) {
                        form.addEventListener('submit', (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            this.handleEmailSubmit();
                        });
                    }
                } else if (step === 2) {
                    // Step 2: Success window
                    const successContainer = windowElement.querySelector('#email-success-window');
                    if (!successContainer) {
                        setTimeout(setupHandlers, 50);
                        return;
                    }

                    const newContainer = successContainer.cloneNode(true);
                    successContainer.parentNode.replaceChild(newContainer, successContainer);

                    newContainer.addEventListener('click', (e) => {
                        e.stopPropagation();
                        const button = e.target.closest('button');
                        if (!button) return;

                        const action = button.dataset.action;
                        if (action === 'close') {
                            this.handleClose();
                        } else if (action === 'shop') {
                            this.handleShopNow();
                        }
                    });
                }
            };

            setupHandlers();
        }

        /**
         * Handle email form submission
         */
        async handleEmailSubmit() {
            const emailInput = document.getElementById('email-capture-input');
            const errorDiv = document.getElementById('email-capture-error');

            if (!emailInput || !emailInput.value) {
                if (errorDiv) {
                    errorDiv.textContent = 'Please enter your email address';
                    errorDiv.style.display = 'block';
                }
                return;
            }

            // Validate email format
            if (!this.validateEmail(emailInput.value)) {
                if (errorDiv) {
                    errorDiv.textContent = 'Please enter a valid email address';
                    errorDiv.style.display = 'block';
                }
                return;
            }

            // Hide error
            if (errorDiv) {
                errorDiv.style.display = 'none';
            }

            this.email = emailInput.value;

            // Submit email to Shopify
            const success = await this.submitEmailToShopify(this.email);
            if (success) {
                // Apply discount code
                this.applyDiscountCode();

                // Update main window to step 2 (keep background errors)
                this.showMainWindow(2);
            } else {
                if (errorDiv) {
                    errorDiv.textContent = 'Failed to submit email. Please try again.';
                    errorDiv.style.display = 'block';
                }
            }
        }

        /**
         * Handle Cancel/Close button
         */
        handleCancel() {
            // Cancel animation if in progress
            this.shouldCancelAnimation = true;

            // Close all windows (including background errors)
            this.closeAllWindows();

            // Show desktop UI immediately after removing error windows
            requestAnimationFrame(() => {
                this.showDesktopUI();
            });
        }

        /**
         * Handle Close button (final step - remove errors and show desktop)
         */
        handleClose() {
            // Cancel animation if in progress
            this.shouldCancelAnimation = true;

            // Close all windows (including background errors)
            this.closeAllWindows();

            // Show desktop UI immediately after removing error windows
            requestAnimationFrame(() => {
                this.showDesktopUI();
            });
        }

        /**
         * Handle Shop Now button (final step - remove errors and show desktop)
         */
        handleShopNow() {
            // Apply discount code
            this.applyDiscountCode();

            // Close all windows (including background errors)
            this.closeAllWindows();

            // Show desktop UI immediately after removing error windows
            requestAnimationFrame(() => {
                this.showDesktopUI();
            });

            // Navigate to home
            window.location.href = '/';
        }

        /**
         * Close all existing windows on screen (before starting email flow)
         */
        closeAllExistingWindows() {
            if (!window.smashOS || !window.smashOS.windowManager) {
                return;
            }

            const windowManager = window.smashOS.windowManager;

            // Get all window IDs from the windows Map
            const allWindowIds = Array.from(windowManager.windows.keys());

            // Close all windows immediately
            allWindowIds.forEach(windowId => {
                const windowElement = document.getElementById(`window-${windowId}`);
                if (windowElement) {
                    // Remove from DOM immediately
                    windowElement.remove();
                }

                // Clean up window manager registry
                if (windowManager.windows) {
                    windowManager.windows.delete(windowId);
                }

                // Remove from taskbar
                if (window.smashOS && window.smashOS.desktopManager) {
                    window.smashOS.desktopManager.removeWindowFromTaskbar(windowId);
                }
            });

            // Clear active window
            if (windowManager.activeWindow) {
                windowManager.activeWindow = null;
            }
        }

        /**
         * Close all email capture windows (including background errors)
         * Used in final step to clean up everything
         * Removes windows immediately without animation delay
         */
        closeAllWindows() {
            if (!window.smashOS || !window.smashOS.windowManager) {
                return;
            }

            const allWindowIds = [...this.windowIds, ...this.errorWindowIds];
            const windowManager = window.smashOS.windowManager;

            // Remove all windows immediately from DOM (skip animation)
            allWindowIds.forEach(windowId => {
                const windowElement = document.getElementById(`window-${windowId}`);
                if (windowElement) {
                    // Remove from DOM immediately
                    windowElement.remove();
                }

                // Clean up window manager registry
                if (windowManager.windows) {
                    windowManager.windows.delete(windowId);
                }

                // Remove from taskbar
                if (window.smashOS && window.smashOS.desktopManager) {
                    window.smashOS.desktopManager.removeWindowFromTaskbar(windowId);
                }
            });

            this.windowIds = [];
            this.errorWindowIds = [];

            // Clean up resize handler
            this.cleanupResizeHandler();
        }

        /**
         * Validate email address
         */
        validateEmail(email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(email);
        }

        /**
         * Submit email to Shopify newsletter
         */
        async submitEmailToShopify(email) {
            return new Promise((resolve) => {
                try {
                    const form = document.getElementById('email-capture-form');
                    if (!form) {
                        console.error('EmailCaptureManager: Form element not found');
                        localStorage.setItem('smash_pending_email', email);
                        resolve(true);
                        return;
                    }

                    const formData = new FormData(form);
                    const submitButton = form.querySelector('button[type="submit"]');
                    const errorDiv = document.getElementById('email-capture-error');

                    // Remove previous errors
                    if (errorDiv) {
                        errorDiv.style.display = 'none';
                        errorDiv.textContent = '';
                    }

                    // Disable submit button
                    if (submitButton) {
                        submitButton.disabled = true;
                        submitButton.textContent = 'SUBMITTING...';
                    }

                    const formAction = form.action || '/contact';

                    fetch(formAction, {
                        method: 'POST',
                        body: formData,
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                        },
                    })
                        .then((response) => {
                            if (response.status === 0 || response.status === 200 || response.status === 302) {
                                console.log('EmailCaptureManager: Email submitted successfully');
                                if (submitButton) {
                                    submitButton.disabled = false;
                                    submitButton.textContent = 'Serve Email';
                                }
                                resolve(true);
                                return;
                            }

                            return response.text().then((html) => {
                                if (!html || html.trim().length === 0) {
                                    if (submitButton) {
                                        submitButton.disabled = false;
                                        submitButton.textContent = 'Serve Email';
                                    }
                                    resolve(true);
                                    return;
                                }

                                const htmlLower = html.toLowerCase();
                                const hasError =
                                    htmlLower.includes('invalid') ||
                                    htmlLower.includes('error') ||
                                    htmlLower.includes('required') ||
                                    htmlLower.includes('already exists');

                                if (hasError && response.status >= 400) {
                                    if (errorDiv) {
                                        errorDiv.textContent = 'Please enter a valid email address.';
                                        errorDiv.style.display = 'block';
                                    }
                                    if (submitButton) {
                                        submitButton.disabled = false;
                                        submitButton.textContent = 'Serve Email';
                                    }
                                    resolve(false);
                                } else {
                                    if (submitButton) {
                                        submitButton.disabled = false;
                                        submitButton.textContent = 'Serve Email';
                                    }
                                    resolve(true);
                                }
                            });
                        })
                        .catch((error) => {
                            console.error('EmailCaptureManager: Form submission error:', error);
                            localStorage.setItem('smash_pending_email', email);
                            if (submitButton) {
                                submitButton.disabled = false;
                                submitButton.textContent = 'Serve Email';
                            }
                            resolve(true);
                        });
                } catch (error) {
                    console.error('EmailCaptureManager: Unexpected error:', error);
                    localStorage.setItem('smash_pending_email', email);
                    resolve(true);
                }
            });
        }

        /**
         * Apply discount code to cart
         */
        applyDiscountCode() {
            // Store discount code in cart attributes
            if (typeof Shopify !== 'undefined' && Shopify.theme && Shopify.theme.cart) {
                fetch('/cart/update.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        attributes: {
                            'discount_code': this.discountCode
                        }
                    })
                }).catch(error => {
                    console.warn('EmailCaptureManager: Could not update cart attributes:', error);
                });
            }

            // Store in localStorage as fallback
            localStorage.setItem('smash_discount_code', this.discountCode);
            localStorage.setItem('smash_discount_applied_date', Date.now().toString());
        }
    }

    // Export to global scope
    window.EmailCaptureManager = EmailCaptureManager;
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
    module.exports = EmailCaptureManager;
}
